import React, { useState } from "react";
import { Phone, Mail, MessageCircle, Send, Loader2 } from "lucide-react";
import axios from "axios";
import { PageHero, Section, Container, H2, GlassCard, Orbs } from "../components/PageUI";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { brand } from "../mock";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://masjid-suite-backend.onrender.com";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", masjid: "", email: "", phone: "", interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Missing info", description: "Please add your name and email so we can get back to you." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      toast({ title: "Thanks, your message has been received.", description: "Our team will follow up within one business day." });
      setForm({ name: "", masjid: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      toast({
        title: "Message not sent",
        description: err?.response?.data?.detail || "Something went wrong. Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "glass-light border-white/60 text-[#1d1d1f] placeholder:text-[#6b7280] h-12 rounded-2xl px-4";

  return (
    <div>
      <SEO
        title="Contact Us | Masjid Suite"
        description="Get in touch with the Masjid Suite team to get started for free, order hardware, or ask a question about your masjid's setup."
        path="/contact"
      />
      <PageHero
        eyebrow="Contact"
        title="Let’s get your masjid"
        gradient="set up."
        lead={<>Tell us a bit about your masjid and what you’re looking for. Our team will follow up within one business day.</>}
      />

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="grid lg:grid-cols-5 gap-6">
            <GlassCard hover={false} className="lg:col-span-3">
              <H2>Send us a message</H2>
              <form onSubmit={submit} className="mt-6 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                  <Input placeholder="Your masjid’s name" value={form.masjid} onChange={(e) => setForm({ ...form, masjid: e.target.value })} className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input type="email" placeholder="you@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                  <Input type="tel" placeholder="(___) ___-____" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                </div>
                <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                  <SelectTrigger className={inputCls}><SelectValue placeholder="What are you interested in?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="software">Free software</SelectItem>
                    <SelectItem value="hardware">Hardware (Kiosk, Mobile POS, Signage Player)</SelectItem>
                    <SelectItem value="website">Website design</SelectItem>
                    <SelectItem value="unsure">Not sure yet</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea placeholder="Tell us a little about your masjid..." rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="glass-light border-white/60 text-[#1d1d1f] placeholder:text-[#6b7280] rounded-2xl px-4 py-3" />
                <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 rounded-full btn-primary font-semibold h-12 disabled:opacity-70">
                  {submitting ? <><Loader2 size={15} className="animate-spin" /> Sending...</> : <>Send Message <Send size={15} /></>}
                </button>
              </form>
            </GlassCard>

            <div className="lg:col-span-2 space-y-4">
              <GlassCard hover={false}>
                <div className="w-10 h-10 rounded-full bg-[#eef3ea] text-[#0e2408] flex items-center justify-center mb-3"><Phone size={16} /></div>
                <div className="font-display text-lg text-[#1d1d1f] font-semibold">Phone</div>
                <div className="text-[13px] text-[#4b5563] mt-1">Talk to our team directly during business hours.</div>
                <a href={`tel:${brand.phoneTel}`} className="mt-3 inline-block text-[#0e2408] font-medium">{brand.phone}</a>
              </GlassCard>
              <GlassCard hover={false}>
                <div className="w-10 h-10 rounded-full bg-[#eef3ea] text-[#0e2408] flex items-center justify-center mb-3"><Mail size={16} /></div>
                <div className="font-display text-lg text-[#1d1d1f] font-semibold">Email</div>
                <div className="text-[13px] text-[#4b5563] mt-1">Prefer to write? Send us your questions any time.</div>
                <a href={`mailto:${brand.email}`} className="mt-3 inline-block text-[#0e2408] font-medium break-all">{brand.email}</a>
              </GlassCard>
              <GlassCard hover={false}>
                <div className="w-10 h-10 rounded-full bg-[#eef3ea] text-[#0e2408] flex items-center justify-center mb-3"><MessageCircle size={16} /></div>
                <div className="font-display text-lg text-[#1d1d1f] font-semibold">WhatsApp</div>
                <div className="text-[13px] text-[#4b5563] mt-1">Message us for a quick answer on your phone.</div>
                <a href={`https://wa.me/${brand.phoneTel.replace('+', '')}`} target="_blank" rel="noreferrer" className="mt-3 inline-block text-[#0e2408] font-medium">Chat on WhatsApp</a>
              </GlassCard>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
